/**
 * Ambassadors — Marketing's relationship screen.
 *
 * Moved here from the events module, where it had been a roster: a name, a home
 * area, and whether they were available. The design asks for the record that
 * actually runs the programme — "send merch, contact them, credit their real
 * handles" — so contact details, a shipping address, a T-shirt size and the
 * per-channel handles are first-class rather than an afterthought.
 *
 * Reads the same `skidaCrmPeople` collection the events module assigns from, and
 * the same assignments collection, so an ambassador's linked events are derived
 * rather than stored twice. The handoff is explicit about that: keep one source
 * of truth per relationship and derive the reverse at read time.
 *
 * Staff are excluded. They live in the same collection so they can be assigned
 * to events, but they are not in the ambassador programme and a roster that
 * mixes them is a roster nobody trusts the count of.
 *
 * The table is the shared `DataTable`, as Leads, Events and Partners are. It was
 * the last hand-written grid apart from the publishing plan, which is why it was
 * one of the two screens with no column filters and no column picker.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  subscribeToEvents,
  createPerson, updatePerson, deletePerson,
  type ScoredEvent,
} from '@/features/events/repository/eventsRepository'
import {
  AMBASSADOR_STATUSES,
  EMPTY_PERSON,
  type EventAssignment,
  type Person,
} from '@/types/events'
import { ListMap, type MapRecord } from '@/shared/map/LazyListMap'
import { ViewToggle, useViewMode } from '@/shared/components/ViewToggle'
import { ColumnPicker, DataTable } from '@/shared/components/DataTable'
import type { DataRow, FilterOptions } from '@/shared/components/DataTable'
import { useTableState } from '@/shared/hooks/useTableState'
import { KanbanBoard, type KanbanCard } from '@/shared/components/KanbanBoard'
import { haystack, matchesTokens } from '@/shared/util/tokenSearch'
import { InfoTip } from '@/shared/ui/InfoTip'
import { nf } from '@/shared/util/numbers'
import { AmbassadorModal } from './AmbassadorModal'
import {
  accessText,
  linkByEmail,
  subscribeAmbassadorAccess,
  ACCESS_LABEL,
  type AccessState,
  type AmbassadorAccess,
} from './repository/ambassadorAccess'
import {
  buildCells,
  COLUMN_KEYS,
  COLUMN_META,
  DEFAULT_COLUMNS,
  DESCENDING_FIRST,
  FILTERABLE_COLUMNS,
  filterValues,
  passesFilters,
  reachLabel,
  sortPeople,
  type ColumnKey,
  type SortKey,
} from './table'
import './AmbassadorsWorkspace.css'

const COLUMNS_KEY = 'skidaCrm.ambassadorColumns'

const styleFor = (s: Person['status']) =>
  AMBASSADOR_STATUSES.find((x) => x.key === s) ?? AMBASSADOR_STATUSES[0]!

export function AmbassadorsWorkspace() {
  const [raw, setRaw] = useState<{ people: Person[]; assignments: EventAssignment[]; events: ScoredEvent[] }>({
    people: [],
    assignments: [],
    events: [],
  })
  const [loading, setLoading] = useState(true)
  const [term, setTerm] = useState('')
  const [status, setStatus] = useState<Person['status'] | 'all'>('all')
  const [viewMode, setViewMode] = useViewMode()
  const [openId, setOpenId] = useState<string | null>(null)
  /** The empty form. Separate from `openId` so a create cannot be mistaken for
   *  an edit of whichever record happened to be open. */
  const [creating, setCreating] = useState(false)

  const t = useTableState<ColumnKey>({
    storageKey: COLUMNS_KEY,
    allKeys: COLUMN_KEYS,
    defaults: DEFAULT_COLUMNS,
    initialSort: 'followers',
    initialDir: 'desc',
    descendingFirst: DESCENDING_FIRST,
  })

  /** Skida Premium per ambassador, from RevenueCat; null until first read. */
  const [access, setAccess] = useState<Map<string, AmbassadorAccess> | null>(null)
  useEffect(() => subscribeAmbassadorAccess(setAccess), [])

  useEffect(() => {
    const un = subscribeToEvents(
      (d) => {
        setRaw({
          people: d.people,
          assignments: d.assignments,
          events: d.events as ScoredEvent[],
        })
        setLoading(false)
      },
      () => setLoading(false),
    )
    return un
  }, [])

  const ambassadors = useMemo(
    () => raw.people.filter((p) => p.role === 'ambassador' || p.role === 'pilot'),
    [raw.people],
  )

  /**
   * Link each ambassador to their app account by email — 27 Oct 2026.
   *
   * Once per page load, for the records never linked or whose email changed
   * since; a uid typed by hand is left alone. One at a time: it is a lookup and
   * a RevenueCat call each, and there are a few dozen.
   */
  const tried = useRef(new Set<string>())
  useEffect(() => {
    if (!access) return
    const todo = ambassadors.filter((p) => {
      const email = p.email.trim().toLowerCase()
      const a = access.get(p.id)
      return email && !tried.current.has(p.id) && (!a || (!a.manual && a.email !== email))
    })
    if (todo.length === 0) return
    for (const p of todo) tried.current.add(p.id)
    void (async () => {
      for (const p of todo) {
        try {
          await linkByEmail(p.id, p.email)
        } catch (e) {
          console.warn('Could not link ambassador to the app', p.id, e)
        }
      }
    })()
  }, [access, ambassadors])

  const accessMap = useMemo(() => access ?? new Map<string, AmbassadorAccess>(), [access])

  const rows = useMemo(() => {
    const matched = ambassadors
      .filter((p) => status === 'all' || p.status === status)
      .filter((p) => passesFilters(p, t.filters, accessMap.get(p.id)))
      .filter((p) =>
        matchesTokens(term, (token) =>
          haystack([
            p.name,
            p.homeArea,
            p.email,
            p.instagram,
            p.campaignCode,
            p.compensation,
            p.contentDelivered,
            p.countries.join(' '),
          ]).includes(token),
        ),
      )
    return sortPeople(matched, t.sortBy as SortKey, t.sortDir, accessMap)
  }, [ambassadors, status, term, t.filters, t.sortBy, t.sortDir, accessMap])

  /**
   * Filter options come from the whole programme, not from what is currently
   * visible — a dropdown whose options disappear as you use it is one you cannot
   * undo.
   */
  const filterOptions = useMemo((): FilterOptions<ColumnKey> => {
    const distinct = (key: ColumnKey) =>
      [...new Set(ambassadors.flatMap((p) => filterValues(p, key)))]
        .sort((a, b) => a.localeCompare(b))
        .map((v) => ({ value: v, label: v }))
    return {
      status: AMBASSADOR_STATUSES.map((s) => ({ value: s.key, label: s.label })),
      premium: (['expiring', 'none', 'active', 'no-account', 'unknown'] as AccessState[]).map((k) => ({
        value: ACCESS_LABEL[k],
        label: ACCESS_LABEL[k],
      })),
      compensation: distinct('compensation'),
      country: distinct('country'),
    }
  }, [ambassadors])

  const dataRows = useMemo(
    (): DataRow<ColumnKey>[] =>
      rows.map((p) => {
        const st = styleFor(p.status)
        return {
          id: p.id,
          title: p.name,
          subtitle: p.homeArea || p.countries.join(', ') || '—',
          cells: buildCells(p, t.columns, accessMap.get(p.id)),
          // The drawn card is name, where, status and the deal — so the deal is
          // named rather than left to whichever columns happen to be switched on.
          card: {
            status: { label: st.label, bg: st.bg, color: st.color },
            // Reach sits on the right of the compensation line — the same
            // value-on-a-meta-line shape the Kanban card uses, so the recipe
            // gains a slot rather than losing a line.
            lines: [
              [p.compensation, reachLabel(p)].filter(Boolean).join(' · '),
              p.campaignCode,
              accessText(accessMap.get(p.id)),
            ],
          },
        }
      }),
    [rows, t.columns, accessMap],
  )

  const open = openId ? ambassadors.find((p) => p.id === openId) ?? null : null

  /** Events this ambassador is assigned to — derived, never stored on the person. */
  const linkedEvents = useMemo(() => {
    if (!open) return []
    const ids = new Set(
      raw.assignments.filter((a) => a.personId === open.id).map((a) => a.eventId),
    )
    return raw.events.filter((e) => ids.has(e.id))
  }, [open, raw.assignments, raw.events])

  const counts = useMemo(() => {
    const m = new Map<string, number>()
    for (const p of ambassadors) m.set(p.status, (m.get(p.status) ?? 0) + 1)
    return m
  }, [ambassadors])

  /**
   * The design's four boxes, with the labels it settled on 14 Aug.
   *
   * They had been placeholders for three rounds, which is why this row was not
   * built: four numbers whose headings do not say what they count are four
   * numbers nobody can act on. Each now says it, and one of them changed
   * meaning in the answering — "With a campaign code" counts the records that
   * actually carry one, not the whole programme.
   *
   * No sparklines and no trend. Nothing writes a daily count of these, and per
   * the same round a curve drawn from today's number is decoration that reads as
   * data; the (i) says so where somebody would look for one.
   */
  const kpis = useMemo(() => {
    const withCode = ambassadors.filter((p) => p.campaignCode.trim()).length
    const known = ambassadors.filter((p) => p.instagramFollowers !== null)
    const reach = known.reduce((n, p) => n + (p.instagramFollowers ?? 0), 0)
    return [
      {
        label: 'Ambassadors on the books',
        value: nf(ambassadors.length),
        info: 'Every ambassador record, whatever their status — the size of the programme on paper. Staff are not counted: they share the collection so they can be assigned to events, and they are not in the programme.',
      },
      {
        label: 'Active this season',
        value: nf(counts.get('active') ?? 0),
        info: 'Status Active — the relationship is running. Prospect, paused and ended records stay on the books and are not counted here.',
      },
      {
        label: 'With a campaign code',
        // Decision 11: a source that works but has recorded nothing yet reads
        // in words. A bare 0 here is indistinguishable from a broken count.
        value: withCode === 0 ? 'None yet' : nf(withCode),
        muted: withCode === 0,
        info: 'Records carrying a tracking code of their own. Without one, an ambassador’s posts cannot be attributed to anything, so this is the share of the programme that is measurable at all.',
      },
      {
        label: 'Combined reach',
        value: reach > 0 ? nf(reach) : 'None yet',
        muted: reach === 0,
        info: `Instagram followers, summed. Read it as a floor: it covers the ${known.length} of ${ambassadors.length} accounts Instagram answers for at all — Business Discovery replies for professional accounts only, and a blank means a personal account rather than no followers. The other platforms need their own APIs, so nothing here is from TikTok or YouTube.`,
      },
    ]
  }, [ambassadors, counts])

  /**
   * The filtered ambassadors, for the map.
   *
   * Thirty-nine of them carry coordinates from their own interest form, which
   * are better than anything geocoding a free-text home area could produce —
   * the geocoder adopts those rather than overwriting them, so most of these
   * pins are where the person said they are.
   */
  const mapRecords = useMemo(
    (): MapRecord[] =>
      rows.map((p) => {
        const st = AMBASSADOR_STATUSES.find((x) => x.key === p.status)
        return {
          id: p.id,
          title: p.name || p.email,
          subtitle: [p.homeArea, p.compensation].filter(Boolean).join(' · '),
          color: st?.color ?? '#4c5b82',
          statusLabel: st?.label ?? p.status,
          geo: p.geo,
          missingReason: p.homeArea || p.postalAddress ? 'could not be placed' : 'no home area on the record',
        }
      }),
    [rows],
  )

  return (
    <main className="amb">
      <div className="amb__head">
        <div>
          <h2 className="amb__title">Ambassadors</h2>
          <p className="amb__sub">
            {loading
              ? 'Loading…'
              : `${ambassadors.length} in the programme · ${rows.length} shown`}
          </p>
        </div>
        {/* Decision 71: a list with no create button is always a defect. The
            programme has only ever grown through the interest form and the
            importer, so there was no way to write down somebody met on a
            mountain. */}
        <button className="btn btn--primary" onClick={() => setCreating(true)}>
          + New ambassador
        </button>
      </div>

      <div className="amb__kpis">
        {kpis.map((k) => (
          <div className="amb__kpi" key={k.label}>
            <span className="amb__kpilabel">
              {k.label}
              <InfoTip label={k.label}>{k.info}</InfoTip>
            </span>
            <span className={`amb__kpivalue${k.muted ? ' amb__kpivalue--none' : ''}`}>
              {k.value}
            </span>
          </div>
        ))}
      </div>

      <div className="amb__filters" onClick={() => t.setOpenFilter(null)}>
        <input
          className="input amb__search"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder="Name, area, handle, campaign code…"
          aria-label="Search ambassadors"
        />
        <button
          className="chip"
          aria-pressed={status === 'all'}
          onClick={() => setStatus('all')}
        >
          All
        </button>
        {AMBASSADOR_STATUSES.map((s) => (
          <button
            key={s.key}
            className="chip"
            aria-pressed={status === s.key}
            onClick={() => setStatus(s.key)}
          >
            {s.label} ({counts.get(s.key) ?? 0})
          </button>
        ))}
        {t.filterCount > 0 && (
          <button className="chip" onClick={t.clearAllFilters}>
            Clear {t.filterCount} filter{t.filterCount === 1 ? '' : 's'}
          </button>
        )}
        <div className="colpickwrap">
          <button
            className="chip"
            onClick={(ev) => {
              ev.stopPropagation()
              t.setShowPicker(!t.showPicker)
            }}
            aria-expanded={t.showPicker}
          >
            Columns ({t.columns.length})
          </button>
          {t.showPicker && (
            <ColumnPicker
              meta={COLUMN_META}
              columns={t.columns}
              onToggle={t.toggleColumn}
              onClose={() => t.setShowPicker(false)}
            />
          )}
        </div>
        <ViewToggle value={viewMode} onChange={setViewMode} label="Ambassadors" withMap />
      </div>

      {viewMode === 'map' ? (
        <ListMap
          records={mapRecords}
          onOpen={(id) => setOpenId(id)}
          emptyNote="No ambassador matches these filters, so there is nothing to place."
        />
      ) : viewMode === 'kanban' ? (
        <KanbanBoard
          columns={AMBASSADOR_STATUSES.map((s) => ({
            key: s.key,
            label: s.label,
            color: s.color,
          }))}
          cards={rows.map(toCard)}
          selectedId={openId}
          onSelect={setOpenId}
          emptyNote={loading ? 'Loading…' : 'No ambassador matches these filters. Clear a filter or widen the search to see the rest of the programme.'}
        />
      ) : (
        <DataTable
          meta={COLUMN_META}
          columns={t.columns}
          rows={dataRows}
          identityLabel="Ambassador"
          identitySortKey="name"
          sortBy={t.sortBy}
          sortDir={t.sortDir}
          onSort={t.onSort}
          filterable={FILTERABLE_COLUMNS}
          filters={t.filters}
          filterOptions={filterOptions}
          onToggleFilter={t.toggleFilter}
          onClearFilter={t.clearFilter}
          openFilter={t.openFilter}
          onOpenFilter={t.setOpenFilter}
          selectedId={openId}
          onSelect={setOpenId}
          loading={loading}
          loadingNote="Loading ambassadors…"
          emptyNote={
            term.trim() || t.filterCount > 0 || status !== 'all'
              ? 'No ambassador matches these filters. Clear a filter or widen the search to see the rest of the programme.'
              : 'Nobody in the programme yet.'
          }
        />
      )}

      {creating && (
        <AmbassadorModal
          person={EMPTY_PERSON}
          linkedEvents={[]}
          onClose={() => setCreating(false)}
          onSave={async (patch) => {
            // Straight into the record once it exists: somebody who has just
            // typed a name is usually about to add the rest.
            const id = await createPerson(patch)
            setCreating(false)
            setOpenId(id)
          }}
          onDelete={async () => setCreating(false)}
        />
      )}

      {open && (
        <AmbassadorModal
          person={open}
          access={accessMap.get(open.id)}
          linkedEvents={linkedEvents}
          onClose={() => setOpenId(null)}
          onSave={(patch) => updatePerson(open.id, patch)}
          onDelete={async (id) => {
            await deletePerson(id)
            setOpenId(null)
          }}
        />
      )}
    </main>
  )
}

function toCard(p: Person): KanbanCard {
  return {
    id: p.id,
    group: p.status,
    title: p.name,
    meta: p.homeArea || p.countries.join(', ') || '—',
    badge: p.instagramFollowers
      ? {
          text: `${p.instagramFollowers.toLocaleString('nb-NO')} IG`,
          bg: 'var(--color-surface-muted)',
          color: 'var(--color-text-muted)',
        }
      : null,
    trailing: p.campaignCode,
  }
}
